import { useState, useEffect } from 'react';
import { db } from '../firebase';
import { 
  collection, 
  onSnapshot, 
  doc, 
  setDoc, 
  updateDoc, 
  query, 
  where, 
  getDocs,
  serverTimestamp,
  increment,
  writeBatch
} from 'firebase/firestore';
import { UserData } from './useUser';
import { MISSIONS, MissionDefinition, MissionType, LIFETIME_CATEGORIES } from '../utils/missions';
import { format } from 'date-fns';

export interface UserMission {
  id: string;
  missionId: string;
  progress: number;
  isClaimed: boolean;
  lastUpdated: any;
  expiresAt: string | null;
}

export function useMissions(
  user: UserData | null, 
  rewards?: { 
    addExp: (amt: number) => Promise<{ leveledUp: boolean, newLevel: number, rewards: any[] } | null>; 
    addCoins: (amt: number) => Promise<void>;
    addTickets: (amt: number) => Promise<void>;
    addBadge: (badgeId: string) => Promise<void>;
    addGems: (amt: number) => Promise<void>;
    addRewardBox: (tier: 'bronze' | 'silver' | 'gold' | 'diamond') => Promise<void>;
  }
) {
  const [missions, setMissions] = useState<UserMission[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      setMissions([]);
      setLoading(false);
      return;
    }

    const missionsRef = collection(db, 'users', user.uid, 'missions');
    
    let isInitialized = false;
    const unsubscribe = onSnapshot(missionsRef, 
      async (snapshot) => {
        const missionData = snapshot.docs.map(doc => ({ 
          id: doc.id, 
          progress: 0,
          isClaimed: false,
          ...doc.data() 
        } as UserMission));
        
        if (!isInitialized) {
          isInitialized = true;
          try {
            // Wait for initialization to complete before updating missions state
            await initializeMissionsIfNeeded(user, missionData);
            await syncLifetimeMissions(user, missionData);
            
              // Special progress updates
              const now = new Date();
              const todayLabel = format(now, 'yyyy-MM-dd');
              const weekLabel = format(now, "yyyy-'W'II");
              
              const progressBatch = writeBatch(db);
              let hasInitUpdates = false;

              if (user.lastLoginDate !== todayLabel) {
                const addLoginProgress = (mId: string, periodStr: string, goal: number) => {
                   const existing = missionData.find(m => m.missionId === mId && (periodStr === 'lifetime' ? true : m.expiresAt === periodStr));
                   const currentProg = existing ? existing.progress : 0;
                   if (currentProg < goal) {
                      progressBatch.set(doc(db, 'users', user.uid, 'missions', `${mId}_${periodStr}`), {
                         missionId: mId, progress: currentProg + 1, lastUpdated: serverTimestamp()
                      }, { merge: true });
                      hasInitUpdates = true;
                   }
                };

                const monthLabel = format(now, 'yyyy-MM');

                addLoginProgress('perfect_week', weekLabel, 7);
                addLoginProgress('dedicated', weekLabel, 5);
                addLoginProgress('numq_veteran', monthLabel, 20);
              }

              if (user.level >= 100) {
                 const existing = missionData.find(m => m.missionId === 'centurion');
                 const currentProg = existing ? existing.progress : 0;
                 if (currentProg < 1) {
                    progressBatch.set(doc(db, 'users', user.uid, 'missions', `centurion_lifetime`), {
                       missionId: 'centurion', progress: 1, lastUpdated: serverTimestamp()
                    }, { merge: true });
                    hasInitUpdates = true;
                 }
              }

              if (hasInitUpdates) {
                 await progressBatch.commit();
              }
            
            // If it was empty, we don't set loading false yet, 
            // the next snapshot with the actual data will handle it.
            if (missionData.length > 0) {
              setMissions(missionData);
              setLoading(false);
            }
          } catch (err) {
            console.error("Mission initialization error:", err);
            setLoading(false);
          }
        } else {
          setMissions(missionData);
          setLoading(false);
        }
      },
      (error: any) => {
        if (error.code === 'permission-denied') {
          // This naturally happens for a split second when a user logs out or deletes their account
          // before the React state updates and unmounts the listener. Safe to ignore.
          return;
        }
        console.error("Missions snapshot error:", error);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [user?.uid, user?.lastLoginDate]);

  const initializeMissionsIfNeeded = async (userData: UserData, currentMissions: UserMission[]) => {
    const now = new Date();
    const todayLabel = format(now, 'yyyy-MM-dd');
    const weekLabel = format(now, "yyyy-'W'II");
    const monthLabel = format(now, 'yyyy-MM');

    // Comprehensive sync: Ensure every mission definition has an active record for the current period
    const missionsToCreate: MissionDefinition[] = [];

    // 1. Dailies
    const dailyDefs = MISSIONS.filter(m => m.type === 'daily');
    dailyDefs.forEach(def => {
        if (!currentMissions.some(m => m.missionId === def.id && m.expiresAt === todayLabel)) {
            missionsToCreate.push(def);
        }
    });

    // 2. Weeklies
    const weeklyDefs = MISSIONS.filter(m => m.type === 'weekly');
    weeklyDefs.forEach(def => {
        if (!currentMissions.some(m => m.missionId === def.id && m.expiresAt === weekLabel)) {
            missionsToCreate.push(def);
        }
    });

    // 3. Monthlies
    const monthlyDefs = MISSIONS.filter(m => m.type === 'monthly');
    monthlyDefs.forEach(def => {
        if (!currentMissions.some(m => m.missionId === def.id && m.expiresAt === monthLabel)) {
            missionsToCreate.push(def);
        }
    });

    // 4. Lifetime
    const lifetimeDefs = MISSIONS.filter(m => m.type === 'lifetime');
    lifetimeDefs.forEach(def => {
        if (!currentMissions.some(m => m.missionId === def.id)) {
            missionsToCreate.push(def);
        }
    });

    // Do not filter out premium missions during creation so everyone has them visible
    const filteredMissions = missionsToCreate;

    if (filteredMissions.length === 0) return;

    const batch = writeBatch(db);
    for (const mission of filteredMissions) {
        const expiresAt = mission.type === 'daily' ? todayLabel 
                        : mission.type === 'weekly' ? weekLabel
                        : mission.type === 'monthly' ? monthLabel 
                        : null;
        
        const missionDocRef = doc(db, 'users', userData.uid, 'missions', `${mission.id}_${expiresAt || 'lifetime'}`);
        batch.set(missionDocRef, {
            missionId: mission.id,
            progress: 0,
            isClaimed: false,
            lastUpdated: serverTimestamp(),
            expiresAt: expiresAt
        }, { merge: true });
    }
    await batch.commit();
  };

  const syncLifetimeMissions = async (userData: UserData, currentMissions: UserMission[]) => {
    const lifetimeMissions = currentMissions.filter(um => {
        const def = MISSIONS.find(d => d.id === um.missionId);
        return def?.type === 'lifetime' && !um.isClaimed;
    });

    const batch = writeBatch(db);
    let hasUpdates = false;

    for (const um of lifetimeMissions) {
        const def = MISSIONS.find(d => d.id === um.missionId);
        if (!def) continue;

        let currentVal = 0;
        if (def.id.startsWith('tutorial_')) {
            if (def.id === 'tutorial_shellcrush') {
                currentVal = userData.completedStandardModes?.includes('shellcrush') ? 1 : 0;
            } else if (def.id === 'tutorial_goldrush') {
                currentVal = userData.completedStandardModes?.includes('goldrush') ? 1 : 0;
            } else if (def.id === 'tutorial_pemdas') {
                currentVal = userData.completedStandardModes?.includes('pemdas') ? 1 : 0;
            } else if (def.id === 'tutorial_starburst') {
                currentVal = userData.completedStandardModes?.includes('starburst') ? 1 : 0;
            } else if (def.id === 'tutorial_graduation') {
                currentVal = userData.isTutorialCompleted ? 1 : 0;
            }
        } else {
            const parts = def.id.split('.');
            if (parts.length !== 2) continue;
            
            const catNum = parseInt(parts[0]) - 1;
            const cat = LIFETIME_CATEGORIES[catNum];
            if (!cat) continue;

            const fieldParts = cat.goalField.split('.');
            let val: any = userData;
            for (const p of fieldParts) { val = val?.[p]; }
            currentVal = val || 0;
        }

        if (currentVal > um.progress) {
            const missionRef = doc(db, 'users', userData.uid, 'missions', um.id);
            batch.update(missionRef, { 
                progress: Math.min(def.goal, currentVal),
                lastUpdated: serverTimestamp()
            });
            hasUpdates = true;
        }
    }

    if (hasUpdates) {
        await batch.commit();
    }
  };


  const updateProgress = async (missionId: string, amount: number = 1) => {
    if (!user) return;

    const now = new Date();
    const todayLabel = format(now, 'yyyy-MM-dd');
    const weekLabel = format(now, "yyyy-'W'II");
    const monthLabel = format(now, 'yyyy-MM');

    const missionDef = MISSIONS.find(m => m.id === missionId);
    if (!missionDef) return;

    const currentPeriodLabel = missionDef.type === 'daily' ? todayLabel :
                             missionDef.type === 'weekly' ? weekLabel :
                             missionDef.type === 'monthly' ? monthLabel : null;

    const activeMission = missions.find(m => 
      m.missionId === missionId && 
      !m.isClaimed && 
      (currentPeriodLabel ? m.expiresAt === currentPeriodLabel : true)
    );

    if (!activeMission) return;

    const newProgress = Math.min(missionDef.goal, activeMission.progress + amount);
    
    const missionRef = doc(db, 'users', user.uid, 'missions', activeMission.id);
    await updateDoc(missionRef, {
      progress: newProgress,
      lastUpdated: serverTimestamp()
    });

    // Handle Milestones automatically
    if (newProgress >= missionDef.goal && activeMission.progress < missionDef.goal) {
       if (missionDef.type === 'daily' && missionId !== 'daily_perfection') checkDailyPerfection(missionId);
       if (missionDef.type === 'weekly' && !missionId.includes('perfection') && !missionId.includes('checkup')) checkWeeklyMilestones(missionId);
       if (missionDef.type === 'monthly' && !missionId.includes('perfection') && !missionId.includes('checkup')) checkMonthlyMilestones(missionId);
    }
  };

  const checkDailyPerfection = async (newlyCompletedId: string) => {
    if (!user) return;
    const todayLabel = format(new Date(), 'yyyy-MM-dd');
    const dailyMissions = missions.filter(m => {
        const def = MISSIONS.find(d => d.id === m.missionId);
        return def?.type === 'daily' && def.id !== 'daily_perfection' && m.expiresAt === todayLabel;
    });

    const completedCount = dailyMissions.filter(m => {
        const def = MISSIONS.find(d => d.id === m.missionId);
        return m.missionId === newlyCompletedId || m.progress >= (def?.goal || 0);
    }).length;

    if (completedCount >= 4) {
        updateProgress('daily_perfection', 1);
    }
  };

  const checkWeeklyMilestones = async (newlyCompletedId: string) => {
    if (!user) return;
    const weekLabel = format(new Date(), "yyyy-'W'II");
    const weeklyMissions = missions.filter(m => {
        const def = MISSIONS.find(d => d.id === m.missionId);
        return def?.type === 'weekly' && !def.id.includes('checkup') && !def.id.includes('perfection') && m.expiresAt === weekLabel;
    });

    const completedCount = weeklyMissions.filter(m => {
        const def = MISSIONS.find(d => d.id === m.missionId);
        return m.missionId === newlyCompletedId || m.progress >= (def?.goal || 0);
    }).length;

    if (completedCount >= 6) updateProgress('weekly_checkup', 1);
    if (completedCount >= 9) updateProgress('weekly_perfection', 1);
  };

  const checkMonthlyMilestones = async (newlyCompletedId: string) => {
    if (!user) return;
    const monthLabel = format(new Date(), 'yyyy-MM');
    const monthlyMissions = missions.filter(m => {
        const def = MISSIONS.find(d => d.id === m.missionId);
        return def?.type === 'monthly' && !def.id.includes('checkup') && !def.id.includes('perfection') && m.expiresAt === monthLabel;
    });

    const completedCount = monthlyMissions.filter(m => {
        const def = MISSIONS.find(d => d.id === m.missionId);
        return m.missionId === newlyCompletedId || m.progress >= (def?.goal || 0);
    }).length;

    if (completedCount >= 14) updateProgress('monthly_checkup', 1);
    if (completedCount >= 17) updateProgress('monthly_perfection', 1);
  };

  const claimReward = async (userMissionId: string) => {
    if (!user) return;

    const userMission = missions.find(m => m.id === userMissionId);
    if (!userMission || userMission.isClaimed) return;

    const missionDef = MISSIONS.find(m => m.id === userMission.missionId);
    if (!missionDef || userMission.progress < missionDef.goal) return;

    // Block claiming if it's a Pro-only mission and the user is not Premium
    if (missionDef.isPremiumOnly && !user.isPremium) return;

    // Process all rewards in the array
    for (const reward of missionDef.rewards) {
        if (rewards) {
            if (reward.type === 'coins') await rewards.addCoins(reward.amount as number);
            else if (reward.type === 'exp') await rewards.addExp(reward.amount as number);
            else if (reward.type === 'tickets') await rewards.addTickets(reward.amount as number);
            else if (reward.type === 'badge') await rewards.addBadge(reward.amount as string);
            else if (reward.type === 'gems') await rewards.addGems(reward.amount as number);
            else if (reward.type === 'box') {
                const subType = reward.subType || 'bronze';
                const amountToAdd = Number(reward.amount) || 1;
                const userRef = doc(db, 'users', user.uid);
                await updateDoc(userRef, { [`rewardBoxes.${subType}`]: increment(amountToAdd) });
            }
            else if (reward.type === 'prism') {
                const stars = reward.stars || 1;
                const userRef = doc(db, 'users', user.uid);
                await updateDoc(userRef, { 
                    [`animaTickets.${stars}`]: increment(Number(reward.amount) || 1) 
                });
            }
            else if (reward.type === 'black_prism') {
                const userRef = doc(db, 'users', user.uid);
                const nextItems = [...(user.inventory?.items || []), 'black_prism'];
                await updateDoc(userRef, { 'inventory.items': nextItems });
            }
            else if (reward.type === 'star_ticket') {
                const userRef = doc(db, 'users', user.uid);
                await updateDoc(userRef, { starTickets: increment(Number(reward.amount) || 1) });
            }
            else if (reward.type === 'vfx') {
                // Placeholder for future VFX rewards
                console.log("VFX reward claimed:", reward.amount);
            }
        } else {
            const userRef = doc(db, 'users', user.uid);
            const updates: any = {};
            if (reward.type === 'coins') updates.coins = increment(reward.amount as number);
            else if (reward.type === 'exp') updates.exp = increment(reward.amount as number);
            else if (reward.type === 'tickets') updates.tickets = increment(reward.amount as number);
            else if (reward.type === 'gems') updates.gems = increment(reward.amount as number);
            await updateDoc(userRef, updates);
        }
    }

    const missionRef = doc(db, 'users', user.uid, 'missions', userMissionId);
    await updateDoc(missionRef, { isClaimed: true });

    // Handle "Routine Maker" if Daily Perfection was claimed
    if (missionDef.id === 'daily_perfection') {
        updateProgress('routine_maker', 1);
    }

    return missionDef.rewards;
  };

  const refresh = async () => {
    if (!user) return;
    setLoading(true);
    try {
      await initializeMissionsIfNeeded(user, missions);
      await syncLifetimeMissions(user, missions);
    } catch (err: any) {
      console.error("Manual refresh error:", err);
      window.alert("Missions Sync Error: " + (err.message || "Unknown error"));
    } finally {
      setLoading(false);
    }
  };

  const claimableCounts = {
    daily: missions.filter(m => {
      const def = MISSIONS.find(d => d.id === m.missionId);
      const isCurrent = def?.type === 'daily' && m.expiresAt === format(new Date(), 'yyyy-MM-dd');
      return isCurrent && !m.isClaimed && m.progress >= (def?.goal || 0);
    }).length,
    weekly: missions.filter(m => {
      const def = MISSIONS.find(d => d.id === m.missionId);
      const isCurrent = def?.type === 'weekly' && m.expiresAt === format(new Date(), "yyyy-'W'II");
      return isCurrent && !m.isClaimed && m.progress >= (def?.goal || 0);
    }).length,
    monthly: missions.filter(m => {
      const def = MISSIONS.find(d => d.id === m.missionId);
      const isCurrent = def?.type === 'monthly' && m.expiresAt === format(new Date(), 'yyyy-MM');
      return isCurrent && !m.isClaimed && m.progress >= (def?.goal || 0);
    }).length,
    lifetime: missions.filter(m => {
      const def = MISSIONS.find(d => d.id === m.missionId);
      return def?.type === 'lifetime' && !m.isClaimed && m.progress >= (def?.goal || 0);
    }).length,
  };

  const totalClaimable = claimableCounts.daily + claimableCounts.weekly + claimableCounts.monthly + claimableCounts.lifetime;

  return { missions, loading, updateProgress, claimReward, refresh, claimableCounts, totalClaimable };
}
